import { motion } from "framer-motion";
import { User, Compass, Building2 } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { useLang } from "@/lib/i18n";

export function About() {
  const { t } = useLang();
  const ROLES = [
    { icon: User, label: t("about.role1") },
    { icon: Compass, label: t("about.role2") },
    { icon: Building2, label: t("about.role3") },
  ];
  return (
    <section id="about" data-testid="about-section" className="py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Chapter index="01" label={t("about.chapter")} testid="about-chapter" />
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.08] tracking-tight text-ivory">
              {t("about.h2a")} <span className="italic text-gold-light">{t("about.h2b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-3">
            {ROLES.map((r) => (
              <span key={r.label} className="flex items-center gap-2 border border-white/10 px-4 py-2.5 text-white/70 hover:border-gold/50 hover:text-gold transition-colors duration-300">
                <r.icon size={14} className="text-gold" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase">{r.label}</span>
              </span>
            ))}
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
          <Reveal delay={0.1}>
            <p className="text-base md:text-lg text-white/60 leading-relaxed">{t("about.p1")}</p>
            <p className="mt-6 text-base md:text-lg text-white/60 leading-relaxed">
              {t("about.p2a")} <span className="text-ivory">BSGOTECH</span> {t("about.p2b")}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function Platform() {
  const { t } = useLang();
  const AREAS = [1, 2, 3, 4].map((n) => ({
    n: `0${n}`,
    name: t(`areas.${n}.name`),
    desc: t(`areas.${n}.desc`),
  }));
  return (
    <section id="bsgotech" data-testid="platform-section" className="py-28 lg:py-40 bg-surface/40 border-y border-white/10 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gold/[0.04] blur-[120px] rounded-full pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Chapter index="02" label={t("platform.chapter")} testid="platform-chapter" />
        <div className="grid lg:grid-cols-12 gap-10 mb-20">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.08] tracking-tight text-ivory">
              {t("platform.h2a")} <span className="italic text-gold-light">{t("platform.h2b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 flex items-end">
            <p className="text-white/60 leading-relaxed text-base md:text-lg">{t("platform.p")}</p>
          </Reveal>
        </div>

        <Reveal className="flex justify-center">
          <div className="border border-gold/40 bg-ink px-12 py-6 text-center relative">
            <span className="font-serif text-3xl tracking-wide text-ivory" data-testid="platform-brand">BSGOTECH</span>
            <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-gold mt-1">{t("platform.tag")}</span>
          </div>
        </Reveal>

        <div className="flex justify-center py-2">
          <motion.div
            className="w-px h-14 bg-gradient-to-b from-gold/70 to-gold/10 origin-top"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {AREAS.map((a, i) => (
            <motion.a
              key={a.n}
              href={`#division-${a.n}`}
              data-testid={`platform-area-${a.n}`}
              className="group bg-ink p-8 lg:p-10 hover:bg-surface transition-colors duration-500"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
            >
              <span className="font-mono text-[11px] tracking-[0.3em] text-gold">{a.n}</span>
              <h3 className="font-serif text-2xl text-ivory mt-4 group-hover:text-gold-light transition-colors duration-300">{a.name}</h3>
              <p className="mt-3 text-sm text-white/50 leading-relaxed">{a.desc}</p>
            </motion.a>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-10 text-center">
          <p data-testid="platform-legal-status" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/35">
            {t("platform.legal")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
